import React, { useEffect, useMemo, useReducer, useState } from "react"
import { ChargingRulesPageContext } from "layouts/charging-rules/context/useChargingRulesContext"
import { useMmsService } from "services/mms/context/MmsServiceContext"
import {
    BackendSchema,
    RuleSchema,
    RuleSchemaType,
    RuleType,
} from "layouts/charging-rules/interface/Rules.ts"
import {
    customEqual,
    updateRuleSchema,
    IsActive,
} from "layouts/charging-rules/utils/utils.ts"
import { rulesReducer } from "layouts/charging-rules/state/rulesRuducer.tsx"

export const ChargingRulesPageProvider = ({
    children,
}: {
    children: React.ReactNode
}) => {
    const { chargingRulesService, rfidRuleService } = useMmsService()
    const { useGetListChargingRules, useUpdateChargingRule } =
        chargingRulesService
    const { useGetRfidCardRule } = rfidRuleService
    const [rfidCardRuleId, setRfidCardRuleId] = useState<string | null>(null)
    const {
        data: rawListChargingRules,
        success: listChargingRulesSuccess,
        sendRequest: fetchListChargingRules,
        isLoading: listChargingRulesLoading,
    } = useGetListChargingRules()
    const {
        success: updateChargingRulesStatus,
        sendRequest: fetchUpdateChargingRules,
        isLoading: updateChargingRuleLoading,
    } = useUpdateChargingRule()

    const {
        sendRequest: fetchRfidCardRule,
        data: rawRfidRule,
        success: getRfidCardRuleSuccess,
        isLoading: getRfidCardRuleLoading,
    } = useGetRfidCardRule(rfidCardRuleId || "")

    const [schema, setSchema] = useState(RuleSchema)
    const [ruleSchemaCompany, setRuleSchemaCompany] = useState(RuleSchema)
    const [errorEnergy, setErrorEnergy] = useState(false)
    const [errorDuration, setErrorDuration] = useState(false)

    const [isRuleChanged, setIsRuleChanged] = useState(true)
    const [rules, dispatch] = useReducer(rulesReducer, []);


    useEffect(() => {
        if (listChargingRulesSuccess && !rfidCardRuleId) {
            const next = updateRuleSchema(RuleSchema, rawListChargingRules as BackendSchema) as RuleSchemaType;
            setSchema(next);
            setRuleSchemaCompany(next);
            dispatch({ type: "initFromSchema", schema: next, companyRule : rawListChargingRules as BackendSchema });
        }
    }, [rfidCardRuleId, listChargingRulesLoading]);

    useEffect(() => {
        if (getRfidCardRuleSuccess && rfidCardRuleId) {
            const next = updateRuleSchema(schema, rawRfidRule as BackendSchema) as RuleSchemaType;
            setSchema(next);
            dispatch({ type: "initFromSchema", schema: next, companyRule : rawListChargingRules as BackendSchema });
        }
    }, [rfidCardRuleId, getRfidCardRuleLoading]);

    useEffect(() => {
        if (rfidCardRuleId) {
            fetchRfidCardRule()
        }
    }, [rfidCardRuleId])

    useEffect(() => {
        if (getRfidCardRuleSuccess && rfidCardRuleId) {
            setSchema((prevSchema) =>
                updateRuleSchema(prevSchema, rawRfidRule as BackendSchema)
            )
        }
    }, [rawRfidRule])

    useEffect(() => {
        setIsRuleChanged(customEqual(rules, schema))
    }, [rules])

    // Refactored: compute all four selection flags from a single memoized block
    const {
        allDaysSelected,
        allHoursSelected,
        noDaysSelected,
        noHoursSelected,
    } = useMemo(() => {
        const findRule = (type: RuleType) =>
            (rules || []).find(r => r.type === type && r.kind === "checkbox");

        const computeAllSelected = (type: RuleType) => {
            const rule = findRule(type);
            if (!rule) return false;
            const opts = (rule.isActive === IsActive.COMPANY ? rule.defaultOptions : rule.options) || {};
            const values = Object.values(opts);
            return values.length > 0 && values.every(Boolean);
        };

        const computeNoneSelected = (type: RuleType) => {
            const rule = findRule(type);
            if (rule?.isActive !== IsActive.TRUE) return false;
            const opts = rule.options || {};
            const values = Object.values(opts);
            return values.length > 0 && values.every(v => !v);
        };

        return {
            allDaysSelected: computeAllSelected(RuleType.DOWBlocked),
            allHoursSelected: computeAllSelected(RuleType.HoursBlocked),
            noDaysSelected: computeNoneSelected(RuleType.DOWBlocked),
            noHoursSelected: computeNoneSelected(RuleType.HoursBlocked),
        };
    }, [rules]);

    return (
        <ChargingRulesPageContext.Provider
            value={{
                rules,
                dispatch,
                schema,
                setSchema,
                fetchListChargingRules,
                listChargingRulesLoading,
                listChargingRulesSuccess,
                fetchUpdateChargingRules,
                updateChargingRuleLoading,
                updateChargingRulesStatus,
                errorEnergy,
                setErrorEnergy,
                errorDuration,
                setErrorDuration,
                rfidCardRuleId,
                allDaysSelected,
                allHoursSelected,
                noDaysSelected,
                noHoursSelected,
                isRuleChanged,
                setIsRuleChanged,
                setRfidCardRuleId,
                getRfidCardRuleLoading,
                ruleSchemaCompany,
            }}
        >
            {children}
        </ChargingRulesPageContext.Provider>
    )
}
